import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Switch from "@mui/material/Switch";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Backdrop from "@mui/material/Backdrop";
import axios from "axios";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import toast from 'react-hot-toast';
import axiosInstance from 'src/axiosInstance';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

// Style constants
const rowStyle = {
  display: "flex",
  alignItems: "center",
  padding: "14px 18px",
  borderBottom: "1px solid #F3F4F6",
  cursor: "pointer",
  background: "#fff",
};
const iconStyle = {
  width: 24,
  height: 24,
  marginRight: 15,
};
const labelStyle = {
  fontWeight: 500,
  fontSize: 16,
  color: "#222",
};
const subLabelStyle = {
  color: "#757575",
  fontSize: 13,
  marginTop: 2,
};
function arrowIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M7.5 5L12.5 10L7.5 15" stroke="#757575" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const SettingsPage = ({ dispatch, user }) => {
  const navigate = useNavigate();
  const [biometricEnabled, setBiometricEnabled] = useState(true);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [view, setView] = useState('main'); // 'main', 'shop-details', 'password', 'rate-us', 'help-support'

  // Shop Details state
  const [storeName, setStoreName] = useState("");
  const [storeCategory, setStoreCategory] = useState("Kiosk");
  const [storeAddress, setStoreAddress] = useState("Line 1");
  const [country, setCountry] = useState("Nigeria");
  const [state, setState] = useState("Lagos");
  const [phoneNumber, setPhoneNumber] = useState("+2348185654614");

  // Password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isPasswordPending, setIsPasswordPending] = useState(false);
  const handleMouseDown = (event) => { event.preventDefault(); };

  // Rate Us state
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const handleRateSubmit = (e) => {
    e.preventDefault();
    console.log('Rating:', rating, 'Feedback:', feedback);
    setView('main');
  };

  // Help & Support state
  const [helpMessage, setHelpMessage] = useState('');
  const [helpPending, setHelpPending] = useState(false);
  const handleHelpSubmit = (e) => {
    e.preventDefault();
    setHelpPending(true);
    setTimeout(() => {
      toast.success('Your message has been sent!');
      setHelpMessage('');
      setHelpPending(false);
      setView('main');
    }, 1200);
  };

  const handleSave = () => {
    console.log("Saving changes:", {
      storeName,
      storeCategory,
      storeAddress,
      country,
      state,
      phoneNumber,
    });
  };

  const handleLogout = () => {
    if (user?.merchant_type) {
      localStorage.removeItem("auth");
      dispatch({ type: "LOGIN", payload: {} });
      setTimeout(() => {
        navigate("/login");
      }, 1000);
    } else {
      axios.get("/admin/auth/logout").then((res) => {
        if (res.data.success) {
          alert(res.data.message); // Replace with toast/Cr.success
          setTimeout(() => navigate("/auth"), 1000);
        } else {
          alert(res.data.message); // Replace with toast/Cr.error
        }
      });
    }
  };

  const handlePasswordSave = async () => {
    setIsPasswordPending(true);
    try {
      const api = axiosInstance || axios;
      const res = await api.post('/merchant/update-password', {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      if (res.status < 300) {
        toast.success('Password reset successful.');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setView('main');
      }
    } catch (err) {
      try {
        toast.error(err.response?.data?.message || 'Something went wrong, please try again later');
      } catch {
        toast.error('Something went wrong, please try again later');
      }
    } finally {
      setIsPasswordPending(false);
    }
  };

  // Shop Details Content
  const ShopDetailsContent = (
    <div
      style={{
        background: "#FFFFFF",
        minHeight: "100vh",
        fontFamily: "Plus Jakarta Sans, sans-serif",
        maxWidth: "100%",
        margin: "0 auto",
        padding: "20px 0",
      }}
    >
      {/* Header */}
      <div style={{ padding: "0 18px 20px" }}>
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          style={{ cursor: "pointer", marginBottom: "10px" }}
          onClick={() => setView('main')}
        >
          <path
            d="M15 18l-6-6 6-6"
            stroke="#757575"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <div>
          <div style={{ fontWeight: 700, fontSize: 23, color: "#222" }}>
            Shop Details
          </div>
          <div style={{ color: "#757575", fontSize: 14, marginTop: 4 }}>
            Here you can view and edit your details.
          </div>
        </div>
      </div>

      {/* Cover Image */}
      <div style={{ margin: "0 18px 20px", borderRadius: 10, overflow: "hidden" }}>
        <div
          style={{
            position: "relative",
            width: "100%",
            height: 180,
            backgroundImage: "url(/assets/Rectangle52.png)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <button
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              border: "none",
              borderRadius: 20,
              padding: "8px 20px",
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Edit Cover Image
          </button>
        </div>
      </div>

      {/* Logo */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          margin: "0 18px 20px",
        }}
      >
        <img
          src="/assets/Rectangle23.png"
          alt="Store Logo"
          style={{ width: 80, height: 80, borderRadius: 10, marginRight: 15 }}
        />
        <div>
          <div style={{ fontWeight: 500, fontSize: 16, color: "#222" }}>
            Edit Store Logo
          </div>
          <div style={{ color: "#9E9E9E", fontSize: 13, marginTop: 2 }}>
            Browse your gallery or use camera to upload
          </div>
        </div>
      </div>

      {/* Form */}
      <div style={{ background: "#fff", borderRadius: 10, margin: "0 18px 20px", padding: 18 }}>
        <TextField
          label="Store Name*"
          variant="outlined"
          fullWidth
          value={storeName}
          onChange={(e) => setStoreName(e.target.value)}
          placeholder="Enter business name"
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
          <InputLabel>Store Category*</InputLabel>
          <Select
            value={storeCategory}
            onChange={(e) => setStoreCategory(e.target.value)}
            label="Store Category*"
          >
            <MenuItem value="Kiosk">Kiosk</MenuItem>
            <MenuItem value="Restaurant">Restaurant</MenuItem>
            <MenuItem value="Cafe">Cafe</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Store Address*"
          variant="outlined"
          fullWidth
          value={storeAddress}
          onChange={(e) => setStoreAddress(e.target.value)}
          placeholder="Line 1"
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
          <InputLabel>Country</InputLabel>
          <Select value={country} onChange={(e) => setCountry(e.target.value)} label="Country">
            <MenuItem value="Nigeria">Nigeria</MenuItem>
            <MenuItem value="USA">USA</MenuItem>
            <MenuItem value="UK">UK</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
          <InputLabel>State</InputLabel>
          <Select value={state} onChange={(e) => setState(e.target.value)} label="State">
            <MenuItem value="Lagos">Lagos</MenuItem>
            <MenuItem value="Abuja">Abuja</MenuItem>
            <MenuItem value="Kano">Kano</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Store Phone Number*"
          variant="outlined"
          fullWidth
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          sx={{ mb: 2 }}
        />
      </div>

      {/* Save Button */}
      <div style={{ padding: "0 18px" }}>
        <button
          onClick={handleSave}
          style={{
            width: "100%",
            padding: 12,
            backgroundColor: "#A0207A",
            color: "white",
            border: "none",
            borderRadius: 8,
            fontSize: 16,
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          Save Changes
        </button>
      </div>
    </div>
  );

  // Password Content
  const PasswordContent = (
    <div
      style={{
        background: "#FFFFFF",
        minHeight: "100vh",
        fontFamily: "Plus Jakarta Sans, sans-serif",
        padding: "20px 0",
        maxWidth: "100%",
        margin: "0 auto",
      }}
    >
      <div style={{ padding: "0 18px 10px 18px" }}>
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          style={{ cursor: "pointer", marginBottom: "10px" }}
          onClick={() => setView('main')}
        >
          <path
            d="M15 18l-6-6 6-6"
            stroke="#757575"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <div style={{ padding: "0 18px 20px 18px", display: "flex", alignItems: "center" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 23, color: "#222" }}>Password</div>
          <div style={{ color: "#757575", fontSize: 14, marginTop: 4 }}>
            This is where you get to change your password and see all passwords available for change.
          </div>
        </div>
      </div>

      <div style={{ margin: "0 18px 20px 18px" }}>
        <div style={{ fontWeight: 600, fontSize: 16, color: "#222", paddingBottom: "10px" }}>Password Info</div>
        <div style={{ color: "#757575", fontSize: 13, marginBottom: "20px" }}>
          View and change your password settings
        </div>

        <TextField
          label="Current Password"
          fullWidth
          variant="outlined"
          type={showCurrent ? "text" : "password"}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowCurrent(!showCurrent)} onMouseDown={handleMouseDown}>
                  {showCurrent ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Enter New Password"
          fullWidth
          variant="outlined"
          type={showNew ? "text" : "password"}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          sx={{ mb: 1 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNew(!showNew)} onMouseDown={handleMouseDown}>
                  {showNew ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <div style={{ color: "#757575", fontSize: 12, marginLeft: 4, marginBottom: 12 }}>
          Your password should have 8 characters
        </div>

        <TextField
          label="Confirm Password"
          fullWidth
          variant="outlined"
          type={showConfirm ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirm(!showConfirm)} onMouseDown={handleMouseDown}>
                  {showConfirm ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>

      <div style={{ padding: "0 18px" }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handlePasswordSave}
          disabled={isPasswordPending}
          sx={{
            backgroundColor: "#A0207A",
            padding: "10px",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: "bold",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "#801A60",
            },
          }}
        >
          {isPasswordPending ? (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ marginRight: 8 }}>Saving...</span>
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            </span>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </div>
  );

  // Rate Us Content
  const RateUsContent = (
    <div
      style={{
        background: '#FFFFFF',
        minHeight: '100vh',
        fontFamily: 'Plus Jakarta Sans, sans-serif',
        padding: '20px 0',
        maxWidth: '100%',
        margin: '0 auto',
      }}
    >
      <div style={{ padding: '0 18px 10px 18px' }}>
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          style={{ cursor: 'pointer', marginBottom: '10px' }}
          onClick={() => setView('main')}
        >
          <path
            d="M15 18l-6-6 6-6"
            stroke="#757575"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div style={{ padding: '0 18px 0 18px' }}>
        <div style={{ fontWeight: 700, fontSize: 23, color: '#222' }}>Rate Us</div>
        <div style={{ color: '#757575', fontSize: 16, marginTop: 4, marginBottom: 32 }}>
          How did you like the app
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
          {[1, 2, 3, 4, 5].map((star) => (
            <span
              key={star}
              style={{ cursor: 'pointer', fontSize: 40, margin: '0 4px' }}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
            >
              {star <= (hoverRating || rating) ? (
                <StarIcon style={{ color: '#af3585', fontSize: 48 }} />
              ) : (
                <StarBorderIcon style={{ color: '#E0E0E0', fontSize: 48 }} />
              )}
            </span>
          ))}
        </div>
        <form onSubmit={handleRateSubmit}>
          <textarea
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            placeholder="Tell us more..."
            style={{
              width: '100%',
              minHeight: 120,
              border: '1px solid #E0E0E0',
              borderRadius: 12,
              padding: 16,
              fontSize: 16,
              marginBottom: 32,
              resize: 'none',
              outline: 'none',
              color: '#222',
              fontFamily: 'inherit',
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: '#af3585',
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 'bold',
              padding: '12px 0',
              marginTop: 2,
              '&:hover': { backgroundColor: '#801A60' },
            }}
          >
            Submit
          </Button>
        </form>
      </div>
    </div>
  );

  // Help & Support Content
  const HelpSupportContent = (
    <div style={{
      background: '#FFFFFF',
      minHeight: '100vh',
      fontFamily: 'Plus Jakarta Sans, sans-serif',
      padding: '20px 0',
      maxWidth: '100%',
      margin: '0 auto',
    }}>
      <div style={{ padding: '0 18px 10px 18px' }}>
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          style={{ cursor: 'pointer', marginBottom: '10px' }}
          onClick={() => setView('main')}
        >
          <path
            d="M15 18l-6-6 6-6"
            stroke="#757575"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div style={{ padding: '0 18px' }}>
        <div style={{ fontWeight: 700, fontSize: 23, color: '#222' }}>Help & Support</div>
        <div style={{ color: '#757575', fontSize: 16, marginTop: 4, marginBottom: 24 }}>
          How can we help you today?
        </div>
        <Button
          fullWidth
          variant="outlined"
          sx={{
            borderColor: '#25D366',
            color: '#25D366',
            fontWeight: 'bold',
            mb: 2,
          }}
          onClick={() => window.open('https://wa.me/2349021667628', '_blank')}
        >
          <WhatsAppIcon sx={{ mr: 1 }} />
          Chat on WhatsApp
        </Button>
        <div style={{ fontWeight: 600, fontSize: 16, color: '#222', margin: '24px 0 10px' }}>Quick FAQ</div>
        <div style={{ color: '#757575', fontSize: 14, marginBottom: 18 }}>
          <b>How do I reset my password?</b><br />
          Go to Settings &gt; Password to change your password.<br /><br />
          <b>How do I contact support?</b><br />
          Use the WhatsApp button above or fill the form below.<br /><br />
          <b>Didn't find your answer?</b>
        </div>
        <form onSubmit={handleHelpSubmit}>
          <textarea
            value={helpMessage}
            onChange={e => setHelpMessage(e.target.value)}
            placeholder="Describe your issue or question..."
            style={{
              width: '100%',
              minHeight: 100,
              border: '1px solid #E0E0E0',
              borderRadius: 12,
              padding: 16,
              fontSize: 16,
              marginBottom: 18,
              resize: 'none',
              outline: 'none',
              color: '#222',
              fontFamily: 'inherit',
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={helpPending}
            sx={{
              backgroundColor: '#af3585',
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 'bold',
              padding: '12px 0',
              marginTop: 2,
              '&:hover': { backgroundColor: '#801A60' },
            }}
          >
            {helpPending ? 'Sending...' : 'Send Message'}
          </Button>
        </form>
      </div>
    </div>
  );

  // Main Settings Content
  const MainSettingsContent = (
    <>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", padding: "0 18px 20px" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 23, color: "#222" }}>Settings</div>
          <div style={{ color: "#757575", fontSize: 14, marginTop: 4 }}>
            View settings and manage them.
          </div>
        </div>
      </div>

      {/* Account Section */}
      <div
        style={{
          background: "#fff",
          borderRadius: 10,
          margin: "0 18px 20px",
          padding: "15px 0",
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 16, color: "#222", padding: "0 18px 10px" }}>
          Account
        </div>

        {/* Shop Details */}
        <div
          style={rowStyle}
          onClick={() => setView('shop-details')}
        >
          <img src="/assets/icons/shop.svg" alt="Shop" style={iconStyle} />
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Shop Details</div>
            <div style={subLabelStyle}>Shop Address, Shop Categories, etc.</div>
          </div>
          {arrowIcon()}
        </div>

        {/* Password */}
        <div
          style={rowStyle}
          onClick={() => setView('password')}
        >
          <img src="/assets/icons/lock.svg" alt="Password" style={iconStyle} />
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Password</div>
            <div style={subLabelStyle}>Update Password</div>
          </div>
          {arrowIcon()}
        </div>
      </div>

      {/* Biometric Toggle */}
      <div style={rowStyle}>
        <img src="/assets/icons/Left Icon.svg" alt="Biometric" style={iconStyle} />
        <div style={{ flex: 1 }}>
          <div style={labelStyle}>Biometric Login</div>
          <div style={subLabelStyle}>Login with fingerprint or Face ID</div>
        </div>
        <Switch
          checked={biometricEnabled}
          onChange={(e) => setBiometricEnabled(e.target.checked)}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: "#A0207A",
              "&:hover": { backgroundColor: "rgba(160, 32, 122, 0.08)" },
            },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#A0207A",
            },
          }}
        />
      </div>

      {/* Rate Us */}
      <div
        style={rowStyle}
        onClick={() => setView('rate-us')}
      >
        <img src="/assets/icons/star.svg" alt="Rate" style={iconStyle} />
        <div style={{ flex: 1 }}>
          <div style={labelStyle}>Rate Us</div>
          <div style={subLabelStyle}>How did you like using the app</div>
        </div>
        {arrowIcon()}
      </div>

      {/* Help & Support */}
      <div
        style={rowStyle}
        onClick={() => setView('help-support')}
      >
        <img src="/assets/icons/profile.svg" alt="Help" style={iconStyle} />
        <div style={{ flex: 1 }}>
          <div style={labelStyle}>Help & Support</div>
          <div style={subLabelStyle}>Make grievance regarding an issue.</div>
        </div>
        {arrowIcon()}
      </div>

      {/* Logout */}
      <div
        style={{ ...rowStyle, marginBottom: 40 }}
        onClick={() => setLogoutOpen(true)}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ marginRight: 15 }}>
          <path
            d="M17 16L21 12L17 8M21 12H9M12 20H4C3.44772 20 3 19.5523 3 19V5C3 4.44772 3.44772 4 4 4H12"
            stroke="#F44336"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500, fontSize: 16, color: "#F44336" }}>Log Out</div>
          <div style={subLabelStyle}>Log out of session</div>
        </div>
        {arrowIcon()}
      </div>

      {/* Logout Modal */}
      <Modal
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
        aria-labelledby="logout-title"
        aria-describedby="logout-description"
      >
        <Fade in={logoutOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography id="logout-title" variant="h6" align="center" fontWeight="bold">
              Logout
            </Typography>
            <Typography id="logout-description" sx={{ mt: 2 }} align="center">
              Do you want to Logout?
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => setLogoutOpen(false)}
                sx={{
                  color: "#222",
                  borderColor: "#E0E0E0",
                  "&:hover": { borderColor: "#A0207A" },
                  width: 120,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleLogout}
                sx={{
                  backgroundColor: "#A0207A",
                  "&:hover": { backgroundColor: "#801A60" },
                  width: 120,
                }}
              >
                Logout
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );

  return view === 'shop-details' ? ShopDetailsContent : view === 'password' ? PasswordContent : view === 'rate-us' ? RateUsContent : view === 'help-support' ? HelpSupportContent : MainSettingsContent;
};

export default SettingsPage;
